import React, { useState, useEffect } from 'react';
import { X, ArrowUpRight, Loader } from 'lucide-react';
import api from '../../services/api';

/**
 * Shared "Convert Lead to Deal" modal for LeadsPage and LeadDetailPage.
 * Requires the user to explicitly pick which pipeline the new Deal lands in,
 * instead of silently falling back to a default/auto-created pipeline.
 */
export default function ConvertToDealModal({ open, lead, onClose, onConfirm }) {
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setAccountName('');
    setLoadingPipelines(true);
    api.get('/crm/pipelines')
      .then(res => {
        const list = res.data.pipelines || [];
        setPipelines(list);
        const preferred = lead?.pipelineId
          ? list.find(p => p._id === (lead.pipelineId._id || lead.pipelineId))
          : null;
        const fallback = list.find(p => p.isDefault) || list[0];
        setPipelineId((preferred || fallback)?._id || '');
      })
      .finally(() => setLoadingPipelines(false));
  }, [open, lead]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!pipelineId) return;
    setSubmitting(true);
    try {
      await onConfirm(pipelineId, accountName.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">Convert to Deal</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <p className="text-[13px] text-[#706E6B]">
            Convert <span className="font-bold text-[#080707]">{lead?.name}</span> into a Deal. Choose which pipeline it should be assigned to.
          </p>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">Pipeline</label>
            {loadingPipelines ? (
              <div className="h-10 flex items-center gap-2 text-[13px] text-[#706E6B]">
                <Loader size={14} className="animate-spin" /> Loading pipelines...
              </div>
            ) : pipelines.length === 0 ? (
              <p className="text-[12px] text-[#BA0517] font-medium">No pipelines found. Create one first under Deals &rarr; Manage Pipelines.</p>
            ) : (
              <select
                value={pipelineId}
                onChange={e => setPipelineId(e.target.value)}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]"
              >
                {pipelines.map(p => (
                  <option key={p._id} value={p._id}>{p.name}{p.isDefault ? ' (Default)' : ''}</option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">Company / Account (optional)</label>
            <input
              type="text"
              value={accountName}
              onChange={e => setAccountName(e.target.value)}
              placeholder="e.g. Acme Corp"
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] outline-none transition-all focus:ring-1 focus:ring-[#0176D3]"
            />
            <p className="text-[11px] text-[#706E6B]">
              Links the new Deal and Contact to this Account, creating it if it doesn't exist yet.
            </p>
          </div>

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
            <button type="button" onClick={onClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!pipelineId || submitting}
              className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
              {submitting ? 'Converting…' : 'Convert Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
